import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildSportsBroadcastEmbed, listSportsBroadcastResources, sportsBroadcastResourceKey,
} from '../_helpers/sportsBroadcastEmbed';

export default function SportsBroadcastPanel({ broadcast, fixtureId }) {
  return <SportsBroadcastFixturePanel key={String(fixtureId)} broadcast={broadcast} fixtureId={fixtureId} />;
}

// The fixture owns selection, including visits via an empty fixture.
function SportsBroadcastFixturePanel({ broadcast, fixtureId }) {
  const box = useRef(null);
  const [width, setWidth] = useState(0);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (!box.current) return undefined;
    const observer = new ResizeObserver(entries => {
      const rect = entries[0]?.contentRect;
      if (rect) setWidth(rect.width);
    });
    observer.observe(box.current);
    return () => observer.disconnect();
  }, []);
  const options = useMemo(() => ({
    hostname: window.location.hostname, playerWidth: width, playerHeight: 300,
  }), [width]);
  const resources = useMemo(() => listSportsBroadcastResources(broadcast.value?.resources ?? [], options),
    [broadcast.value?.resources, options]);
  const resource = resources.find(item => selected?.fixtureId === fixtureId
    && sportsBroadcastResourceKey(item) === selected.key) ?? resources[0];
  const resourceKey = resource ? sportsBroadcastResourceKey(resource) : null;
  // Pin an admitted selection by identity, not array position. Poll reordering
  // cannot reload a playing iframe, and removed resources do not steal it back.
  if (width >= 200) {
    if (resourceKey && (selected?.fixtureId !== fixtureId || selected?.key !== resourceKey)) {
      setSelected({ fixtureId, key: resourceKey });
    } else if (!resourceKey && broadcast.value?.state === 'AVAILABLE' && selected !== null) {
      setSelected(null);
    }
  }
  const embed = resource ? buildSportsBroadcastEmbed(resource, options) : null;
  const playable = !broadcast.closed && broadcast.value?.state === 'AVAILABLE' && width >= 200 && embed;
  const playerHeight = resource?.provider === 'twitch' ? 300 : 200;
  // Keep a zero-height measuring slot mounted: display:none/null would prevent
  // ResizeObserver from ever admitting a player after initial/insufficient width.
  return (
    <div ref={box} className="ipp_broadcast_slot">
    {(playable || broadcast.closed) && <section className="ipp_broadcast" aria-label="e스포츠 경기 방송">
      <div className="ipp_broadcast_controls">
        {!broadcast.closed && resources.map(item => (
          <button type="button" key={sportsBroadcastResourceKey(item)}
            aria-pressed={sportsBroadcastResourceKey(resource) === sportsBroadcastResourceKey(item)}
            onClick={() => setSelected({ fixtureId, key: sportsBroadcastResourceKey(item) })}>{item.displayLabel}</button>
        ))}
        <button className="ipp_broadcast_toggle" type="button" onClick={broadcast.closed ? broadcast.reopen : broadcast.close}>
          {broadcast.closed ? '방송 다시 열기' : '방송 닫기'}
        </button>
      </div>
      {playable && (
        <div className="ipp_broadcast_player" style={{ height: playerHeight }}>
          <iframe
            key={`${fixtureId}:${sportsBroadcastResourceKey(resource)}`}
            src={embed.src} title={embed.title} allow={embed.allow} allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
          />
        </div>
      )}
    </section>}
    </div>
  );
}
